import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api, { API_BASE_URL } from '../services/api';
import ComparisonTab from '../components/ComparisonTab';
import ScenariosTab from '../components/ScenariosTab';
import LookbackTab from '../components/LookbackTab';
import TerminatedPlanWarning from '../components/TerminatedPlanWarning';
import NextStepsTab from '../components/NextStepsTab';
import PlanCharacteristicsCard from '../components/PlanCharacteristicsCard';

const PlanDetails = () => {
  const { ein, planNumber, planYear } = useParams();
  const navigate = useNavigate();
  const [planDetails, setPlanDetails] = useState(null);
  const [planStatus, setPlanStatus] = useState(null);
  const [historicalData, setHistoricalData] = useState([]);
  const [contributionCostData, setContributionCostData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchPlanData();
  }, [ein, planNumber, planYear]);

  const fetchPlanData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch enhanced plan details with characteristics
      const response = await fetch(`${API_BASE_URL}/plan/${ein}/${planNumber}/${planYear}/details`);
      if (!response.ok) {
        throw new Error('Failed to fetch plan details');
      }
      const details = await response.json();
      setPlanDetails(details);
      
      // Fetch plan status (terminated/active/inactive)
      try {
        const statusResponse = await fetch(`${API_BASE_URL}/plan-status/${ein}/${planNumber}`);
        if (statusResponse.ok) {
          const status = await statusResponse.json();
          setPlanStatus(status);
        }
      } catch (err) {
        console.warn('Could not fetch plan status:', err);
        setPlanStatus(null);
      }
      
      // Fetch historical data
      const history = await api.getPlanHistory(ein, planNumber);
      setHistoricalData(history);

      // Fetch contribution cost analysis data
      try {
        const contributionCost = await api.getContributionCostAnalysis(ein, planNumber);
        setContributionCostData(contributionCost);
      } catch (err) {
        console.warn('Contribution cost analysis not available:', err);
        setContributionCostData([]);
      }
    } catch (err) {
      console.error('Error fetching plan data:', err);
      setError('Failed to load plan details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCurrencyDetailed = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Historical S&P 500 annual returns (total return including dividends)
  const sp500Returns = {
    2009: 0.2646,  // +26.46%
    2010: 0.1506,  // +15.06%
    2011: 0.0211,  // +2.11%
    2012: 0.1600,  // +16.00%
    2013: 0.3239,  // +32.39%
    2014: 0.1369,  // +13.69%
    2015: 0.0138,  // +1.38%
    2016: 0.1196,  // +11.96%
    2017: 0.2183,  // +21.83%
    2018: -0.0438, // -4.38%
    2019: 0.3143,  // +31.43%
    2020: 0.1840,  // +18.40%
    2021: 0.2871,  // +28.71%
    2022: -0.1803, // -18.03%
    2023: 0.2643,  // +26.43%
    2024: 0.2500,  // +25.00% (approximate, year still in progress)
  };

  // Calculate investment performance metrics from historical data
  const calculateInvestmentPerformance = () => {
    if (historicalData.length < 2) return [];
    
    // Sort by year ascending for chronological calculation
    const sortedData = [...historicalData].sort((a, b) => a.plan_year - b.plan_year);
    const performanceData = [];
    
    for (let i = 1; i < sortedData.length; i++) {
      const currentYear = sortedData[i];
      const previousYear = sortedData[i - 1];
      
      const currentAssets = currentYear.assets || 0;
      const previousAssets = previousYear.assets || 0;
      const contributions = currentYear.contributions || 0;
      
      if (previousAssets > 0) {
        // Net Growth % = (Current Assets - Previous Assets) / Previous Assets
        const netGrowthPct = (currentAssets - previousAssets) / previousAssets;
        
        // Investment Return % = (Current Assets - Previous Assets - Contributions) / Previous Assets
        const investmentReturn = (currentAssets - previousAssets - contributions) / previousAssets;
        
        // Get S&P 500 return for this year (if available)
        const sp500Return = sp500Returns[currentYear.plan_year] || null;
        
        performanceData.push({
          plan_year: currentYear.plan_year,
          investment_return: investmentReturn,
          net_growth_pct: netGrowthPct,
          sp500_return: sp500Return,
        });
      }
    }
    
    return performanceData;
  };

  const investmentPerformance = calculateInvestmentPerformance();

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-16 w-16 border-4 border-teal rounded-full border-t-transparent mx-auto"></div>
          <p className="mt-6 text-neutral-700 text-xl font-medium">Loading plan details...</p>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-card p-10 max-w-md border border-neutral-200">
          <div className="text-center">
            <svg className="mx-auto h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="mt-6 text-2xl font-bold text-neutral-900">Error Loading Plan</h2>
            <p className="mt-3 text-neutral-700">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-8 px-8 py-3 bg-teal text-white rounded-lg hover:bg-teal-dark transition-colors font-semibold shadow-md"
            >
              Back to Search
            </button>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview' },
    { id: 'history', name: 'Historical Data' },
    { id: 'comparison', name: 'Peer Comparison' },
    { id: 'lookback', name: 'Historical Impact' },
    { id: 'scenarios', name: 'Future Scenarios' },
    { id: 'next-steps', name: 'Next Steps' },
  ];

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Header */}
      <div className="bg-white shadow-md border-b-4 border-teal">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="mb-4 text-teal-dark hover:text-teal-dark flex items-center font-medium text-base transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Search
          </button>
          
          <h1 className="text-4xl font-bold text-neutral-900 mb-2">
            {planDetails.plan_name || `Plan ${planDetails.plan_number}`}
          </h1>
          <p className="text-xl text-neutral-700">
            {planDetails.sponsor_name}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Terminated Plan Warning */}
        {planStatus && (
          <TerminatedPlanWarning 
            terminationYear={planStatus.termination_year}
            latestActiveYear={planStatus.latest_active_year || planStatus.latest_filing_year}
            planStatus={planStatus.plan_status}
          />
        )}

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-soft p-6 border-l-4 border-neutral-400">
            <p className="text-sm text-neutral-600 font-semibold uppercase tracking-wide mb-2">Plan Year</p>
            <p className="text-4xl font-bold text-neutral-900">{planDetails.plan_year}</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-soft p-6 border-l-4 border-teal">
            <p className="text-sm text-neutral-600 font-semibold uppercase tracking-wide mb-2">Participants</p>
            <p className="text-4xl font-bold text-teal-dark">
              {formatNumber(planDetails.participants)}
            </p>
            <p className="text-xs text-neutral-500 mt-2">With account balances</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-soft p-6 border-l-4 border-green-600">
            <p className="text-sm text-neutral-600 font-semibold uppercase tracking-wide mb-2">Total Assets</p>
            <p className="text-4xl font-bold text-green-600">
              {formatCurrency(planDetails.assets)}
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-soft p-6 border-l-4 border-teal">
            <p className="text-sm text-neutral-600 font-semibold uppercase tracking-wide mb-2">Cost/Participant</p>
            <p className="text-4xl font-bold text-teal-dark">
              {formatCurrencyDetailed(planDetails.cost_per_participant)}
            </p>
            <p className="text-xs text-neutral-500 mt-2">Administrative expenses</p>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-t-2xl shadow-soft border border-neutral-200 border-b-0 overflow-x-auto">
          <div className="flex min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-8 py-5 font-semibold text-base transition-all border-b-4 ${
                  activeTab === tab.id
                    ? 'text-teal-dark border-teal bg-neutral-50'
                    : 'text-neutral-600 border-transparent hover:text-neutral-900 hover:bg-neutral-50'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-b-2xl shadow-soft p-8 border border-neutral-200">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div>
              <h2 className="text-3xl font-bold text-neutral-900 mb-8">Plan Overview</h2>
              
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                <div className="bg-neutral-50 rounded-lg p-6 border border-neutral-200">
                  <h3 className="text-xl font-bold text-neutral-900 mb-4 pb-3 border-b-2 border-neutral-300">
                    Plan Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-neutral-600 font-medium">EIN</p>
                      <p className="text-lg font-semibold text-neutral-900">{planDetails.ein}</p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600 font-medium">Plan Number</p>
                      <p className="text-lg font-semibold text-neutral-900">{planDetails.plan_number}</p>
                    </div>
                    {planDetails.city && planDetails.state && (
                      <div>
                        <p className="text-sm text-neutral-600 font-medium">Location</p>
                        <p className="text-lg font-semibold text-neutral-900">{planDetails.city}, {planDetails.state}</p>
                      </div>
                    )}
                    {planDetails.plan_eff_date && (
                      <div>
                        <p className="text-sm text-neutral-600 font-medium">Plan Effective Date</p>
                        <p className="text-lg font-semibold text-neutral-900">
                          {new Date(planDetails.plan_eff_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-neutral-50 rounded-lg p-6 border border-neutral-200">
                  <h3 className="text-xl font-bold text-neutral-900 mb-4 pb-3 border-b-2 border-neutral-300">
                    Financial Summary
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-neutral-600 font-medium">Contributions</p>
                      <p className="text-lg font-semibold text-green-600">{formatCurrency(planDetails.total_contributions)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600 font-medium">Admin Expenses</p>
                      <p className="text-lg font-semibold text-teal-dark">{formatCurrency(planDetails.admin_expenses)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600 font-medium">Avg Balance</p>
                      <p className="text-lg font-semibold text-teal-dark">
                        {planDetails.assets && planDetails.participants 
                          ? formatCurrency(planDetails.assets / planDetails.participants)
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Historical Charts */}
              {historicalData.length > 0 && (
                <>
                  <div className="mb-10">
                    <h3 className="text-2xl font-bold text-neutral-900 mb-6">Asset Growth Over Time</h3>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={[...historicalData].reverse()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                        <XAxis 
                          dataKey="plan_year" 
                          stroke="#666"
                          style={{ fontSize: '14px', fontWeight: '500' }}
                        />
                        <YAxis 
                          stroke="#666"
                          tickFormatter={(value) => {
                            if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
                            if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
                            return `$${value}`;
                          }}
                          style={{ fontSize: '14px', fontWeight: '500' }}
                        />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ backgroundColor: 'white', border: '2px solid #E5E5E5', borderRadius: '8px' }}
                          labelStyle={{ fontWeight: 'bold', color: '#1A1A1A' }}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px' }}
                          iconType="line"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="assets" 
                          stroke="#12A493" 
                          strokeWidth={4}
                          name="Total Assets"
                          dot={{ r: 4, fill: '#12A493' }}
                          activeDot={{ r: 7 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="mb-10">
                    <h3 className="text-2xl font-bold text-neutral-900 mb-6">Participant Growth</h3>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={[...historicalData].reverse()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                        <XAxis 
                          dataKey="plan_year" 
                          stroke="#666"
                          style={{ fontSize: '14px', fontWeight: '500' }}
                        />
                        <YAxis 
                          stroke="#666"
                          style={{ fontSize: '14px', fontWeight: '500' }}
                        />
                        <Tooltip 
                          formatter={(value) => formatNumber(value)}
                          contentStyle={{ backgroundColor: 'white', border: '2px solid #E5E5E5', borderRadius: '8px' }}
                          labelStyle={{ fontWeight: 'bold', color: '#1A1A1A' }}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px' }}
                          iconType="line"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="participants" 
                          stroke="#FF7A5C" 
                          strokeWidth={4}
                          name="Participants"
                          dot={{ r: 4, fill: '#FF7A5C' }}
                          activeDot={{ r: 7 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}

              {/* Contribution Cost Analysis */}
              {contributionCostData.length > 0 && (
                <div className="mt-10">
                  <h3 className="text-2xl font-bold text-neutral-900 mb-6">Cost Burden Analysis</h3>
                  
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-amber-500 rounded-lg p-6 mb-8">
                    <p className="text-base text-neutral-800 leading-relaxed">
                      <strong>Understanding Fee Burden:</strong> This chart shows administrative costs 
                      as a percentage of total contributions. Lower percentages mean more of your 
                      contributions go into participant accounts, not to fees.
                    </p>
                  </div>

                  <div className="mb-10">
                    <h3 className="text-xl font-semibold text-neutral-900 mb-4">
                      Admin Costs as % of Contributions
                    </h3>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={[...contributionCostData].reverse()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                        <XAxis 
                          dataKey="plan_year" 
                          stroke="#666"
                          style={{ fontSize: '14px', fontWeight: '500' }}
                        />
                        <YAxis 
                          stroke="#666"
                          tickFormatter={(value) => `${value.toFixed(1)}%`}
                          style={{ fontSize: '14px', fontWeight: '500' }}
                        />
                        <Tooltip 
                          formatter={(value) => formatPercent(value)}
                          contentStyle={{ backgroundColor: 'white', border: '2px solid #E5E5E5', borderRadius: '8px' }}
                          labelStyle={{ fontWeight: 'bold', color: '#1A1A1A' }}
                        />
                        <Legend wrapperStyle={{ paddingTop: '20px' }} />
                        <Bar 
                          dataKey="admin_cost_as_pct_of_total_contrib" 
                          fill="#FF7A5C" 
                          name="Admin Cost %"
                          radius={[8, 8, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                    <p className="text-sm text-neutral-600 mt-4 leading-relaxed">
                      This shows what percentage of contributions is consumed by administrative fees. 
                      Lower is better - more money goes into participant accounts.
                    </p>
                  </div>

                  <div className="mb-10">
                    <h3 className="text-xl font-semibold text-neutral-900 mb-4">
                      Contribution vs Admin Cost Breakdown
                    </h3>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={[...contributionCostData].reverse()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                        <XAxis 
                          dataKey="plan_year" 
                          stroke="#666"
                          style={{ fontSize: '14px', fontWeight: '500' }}
                        />
                        <YAxis 
                          stroke="#666"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                          style={{ fontSize: '14px', fontWeight: '500' }}
                        />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ backgroundColor: 'white', border: '2px solid #E5E5E5', borderRadius: '8px' }}
                          labelStyle={{ fontWeight: 'bold', color: '#1A1A1A' }}
                        />
                        <Legend wrapperStyle={{ paddingTop: '20px' }} />
                        <Bar 
                          dataKey="total_contributions" 
                          fill="#10b981" 
                          name="Contributions"
                          radius={[8, 8, 0, 0]}
                        />
                        <Bar 
                          dataKey="admin_expenses" 
                          fill="#FF7A5C" 
                          name="Admin Costs"
                          radius={[8, 8, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                    <p className="text-sm text-neutral-600 mt-4 leading-relaxed">
                      Side-by-side comparison of total contributions (green) vs. total admin costs (coral). 
                      Lower admin costs mean more money stays in participant accounts.
                    </p>
                  </div>

                  <div className="mb-10">
                    <h3 className="text-xl font-semibold text-neutral-900 mb-4">
                      Admin Costs as % of Plan Assets
                    </h3>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={[...contributionCostData].reverse()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                        <XAxis 
                          dataKey="plan_year" 
                          stroke="#666"
                          style={{ fontSize: '14px', fontWeight: '500' }}
                        />
                        <YAxis 
                          stroke="#666"
                          tickFormatter={(value) => `${value.toFixed(2)}%`}
                          domain={[0, 'auto']}
                          style={{ fontSize: '14px', fontWeight: '500' }}
                        />
                        <Tooltip 
                          formatter={(value) => formatPercent(value)}
                          contentStyle={{ backgroundColor: 'white', border: '2px solid #E5E5E5', borderRadius: '8px' }}
                          labelStyle={{ fontWeight: 'bold', color: '#1A1A1A' }}
                        />
                        <Legend wrapperStyle={{ paddingTop: '20px' }} />
                        <Line 
                          type="monotone" 
                          dataKey="admin_cost_as_pct_of_assets" 
                          stroke="#7C5CDB" 
                          strokeWidth={4}
                          name="Admin Costs as % of Assets"
                          dot={{ r: 5, fill: '#7C5CDB' }}
                          activeDot={{ r: 8 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                    <p className="text-sm text-neutral-600 mt-4 leading-relaxed">
                      This shows annual administrative expenses as a percentage of total plan assets 
                      (similar to an expense ratio). Many 401(k) plans target total costs 
                      (admin + investment) under 1% of assets annually.
                    </p>
                  </div>

                  {/* Benchmark Reference */}
                  <div className="bg-neutral-50 rounded-lg p-8 border-2 border-neutral-200">
                    <h4 className="text-2xl font-bold text-neutral-900 mb-6">Fee Burden Benchmarks</h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                      <div className="text-center">
                        <div className="text-lg font-bold text-neutral-700 mb-2">&lt; 1%</div>
                        <div className="text-xl font-bold text-green-600 mb-2">Excellent</div>
                        <div className="text-sm text-neutral-600">Best in class</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-neutral-700 mb-2">1% - 2%</div>
                        <div className="text-xl font-bold text-blue-600 mb-2">Good</div>
                        <div className="text-sm text-neutral-600">Competitive</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-neutral-700 mb-2">2% - 5%</div>
                        <div className="text-xl font-bold text-amber-600 mb-2">Moderate</div>
                        <div className="text-sm text-neutral-600">Room for improvement</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-neutral-700 mb-2">5% - 10%</div>
                        <div className="text-xl font-bold text-orange-600 mb-2">High</div>
                        <div className="text-sm text-neutral-600">Review recommended</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-neutral-700 mb-2">&gt; 10%</div>
                        <div className="text-xl font-bold text-red-600 mb-2">Very High</div>
                        <div className="text-sm text-neutral-600">Action needed</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Plan Characteristics Section */}
              {planDetails?.plan_characteristics && (
                <div className="bg-white border-2 border-neutral-200 rounded-2xl p-8 mt-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-teal-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-neutral-900">Plan Characteristics</h3>
                  </div>
                  
                  <PlanCharacteristicsCard 
                    characteristics={planDetails.plan_characteristics}
                    businessDescription={planDetails.business_description}
                  />
                </div>
              )}
            </div>
          )}

          {/* Historical Data Tab */}
          {activeTab === 'history' && (
            <div>
              <h2 className="text-3xl font-bold text-neutral-900 mb-8">Historical Plan Data</h2>
              
              {/* Investment Performance Chart */}
              {investmentPerformance.length > 0 && (
                <div className="bg-white border-2 border-neutral-200 rounded-2xl p-8 mb-8">
                  <h3 className="text-xl font-bold text-neutral-900 mb-6">
                    Investment Performance vs. Market Benchmark
                  </h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={investmentPerformance}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                      <XAxis 
                        dataKey="plan_year" 
                        stroke="#737373"
                        style={{ fontSize: '14px' }}
                      />
                      <YAxis 
                        stroke="#737373"
                        style={{ fontSize: '14px' }}
                        tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                      />
                      <Tooltip 
                        formatter={(value) => `${(value * 100).toFixed(2)}%`}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '2px solid #e5e5e5',
                          borderRadius: '12px',
                          padding: '12px'
                        }}
                      />
                      <Legend />
                      
                      {/* S&P 500 Benchmark - Draw first so it's in the background */}
                      <Line
                        type="monotone"
                        dataKey="sp500_return"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="S&P 500 Benchmark (%)"
                        dot={{ fill: '#f59e0b', r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                      
                      {/* Plan Investment Return */}
                      <Line
                        type="monotone"
                        dataKey="investment_return"
                        stroke="#06b6d4"
                        strokeWidth={3}
                        name="Your Plan's Investment Return (%)"
                        dot={{ fill: '#06b6d4', r: 5 }}
                        activeDot={{ r: 7 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2 text-sm text-neutral-600">
                    <p className="italic">
                      <strong>Your Plan's Investment Return</strong> (cyan) shows pure investment performance excluding contributions.
                    </p>
                    <p className="italic">
                      <strong>S&P 500 Benchmark</strong> (orange dashed) shows how a simple market index fund performed for comparison.
                    </p>
                    {investmentPerformance.some(d => d.sp500_return && d.investment_return < d.sp500_return - 0.05) && (
                      <p className="text-amber-700 font-semibold mt-3 bg-amber-50 p-3 rounded-lg border border-amber-200">
                        ⚠️ Your plan significantly underperformed the S&P 500 in one or more years. 
                        This may indicate high fees, poor fund selection, or excessive trading costs.
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {historicalData.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border-2 border-neutral-200">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-bold text-neutral-700 uppercase tracking-wider">
                          Year
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-neutral-700 uppercase tracking-wider">
                          Participants
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-neutral-700 uppercase tracking-wider">
                          Assets
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-neutral-700 uppercase tracking-wider">
                          Contributions
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-neutral-700 uppercase tracking-wider">
                          Admin Expenses
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-neutral-700 uppercase tracking-wider">
                          Cost/Participant
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      {historicalData.map((year) => (
                        <tr key={year.plan_year} className="hover:bg-neutral-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-base font-bold text-neutral-900">
                            {year.plan_year}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-base text-neutral-900">
                            {formatNumber(year.participants)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-base text-neutral-900">
                            {formatCurrency(year.assets)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-base text-neutral-900">
                            {formatCurrency(year.contributions)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-base text-neutral-900">
                            {formatCurrency(year.admin_expenses)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-base font-bold text-teal-dark">
                            {formatCurrency(year.cost_per_participant)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-16 bg-neutral-50 rounded-lg border-2 border-neutral-200">
                  <p className="text-xl text-neutral-600">No historical data available</p>
                </div>
              )}
            </div>
          )}

          {/* Peer Comparison Tab */}
          {activeTab === 'comparison' && (
            <ComparisonTab ein={ein} planNumber={planNumber} />
          )}

          {/* Historical Impact Tab */}
          {activeTab === 'lookback' && (
            <LookbackTab 
              ein={ein} 
              planNumber={planNumber} 
              planStatus={planStatus}
            />
          )}

          {/* Scenario Analysis Tab */}
          {activeTab === 'scenarios' && (
            <ScenariosTab 
              ein={ein} 
              planNumber={planNumber}
              planStatus={planStatus}
            />
          )}

          {/* Next Steps Tab */}
          {activeTab === 'next-steps' && (
            <NextStepsTab 
              ein={ein} 
              planNumber={planNumber}
              planYear={planYear}
              planDetails={planDetails}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanDetails;
